import * as React from 'react';
import { SimpleGrid, Table } from '@chakra-ui/react';
import {
  Heading,
  HStack,
  Text,
  VStack,
} from '@ayble-health/dashboard-components';
import { Section } from './Section';
import { Tile } from './Tile';
import {
  breakpointTokens,
  fontSizeTokens,
  fontTokens,
  fontWeightTokens,
} from '../../theme/chakra';
import {
  TYPE_USAGE_MEASURED_ON,
  USED_FONT_SIZES,
  USED_FONT_WEIGHTS,
} from './typeScaleUsage';

const SAMPLE = 'Ayble Health';

const BODY_SAMPLE =
  'Track allergies, sensitivities, and preferences across every visit.';

/**
 * What you get without passing anything.
 *
 * This leads the section because it is what almost every call site actually
 * renders — the scale below is the set you can opt into, not the set in use.
 * Read off the shared `Text` and `Heading` wrappers rather than restated by
 * hand, so it cannot drift from what the package does.
 */
const Defaults = () => (
  <VStack gap={3} align="stretch">
    <Tile
      label="Text — no props"
      note="fontSize sm (14px) · fontWeight 300 · color muted.600 · lineHeight base · my 0"
    >
      <Text>{BODY_SAMPLE}</Text>
    </Tile>
    <Tile
      label="Heading — no props"
      note="fontWeight medium (500) · lineHeight shorter · Recoleta"
    >
      <Heading>{SAMPLE}</Heading>
    </Tile>
  </VStack>
);

const Fonts = () => (
  <VStack gap={3} align="stretch">
    {Object.entries(fontTokens).map(([token, { value }]) => (
      <HStack key={token} gap={4} align="baseline">
        <Text minW="80px" fontSize="xs" color="muted.400">
          {token}
        </Text>
        <Text fontFamily={token} fontSize="2xl" color="muted.900">
          {SAMPLE}
        </Text>
        <Text fontSize="2xs" color="muted.400">
          {value}
        </Text>
      </HStack>
    ))}
  </VStack>
);

const FontSizes = () => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
    {Object.entries(fontSizeTokens)
      .filter(([token]) =>
        (USED_FONT_SIZES as readonly string[]).includes(token)
      )
      .map(([token, { value }]) => (
        <Tile key={token} label={token} note={value}>
          <Text fontSize={token} color="muted.900" my={0}>
            {SAMPLE}
          </Text>
        </Tile>
      ))}
  </SimpleGrid>
);

const FontWeights = () => (
  <SimpleGrid columns={{ base: 1, md: 2 }} gap={3}>
    {Object.entries(fontWeightTokens)
      .filter(([token]) =>
        (USED_FONT_WEIGHTS as readonly string[]).includes(token)
      )
      .map(([token, { value }]) => (
        <Tile key={token} label={token} note={value}>
          <Text fontWeight={token} fontSize="lg" color="muted.900" my={0}>
            {SAMPLE}
          </Text>
        </Tile>
      ))}
  </SimpleGrid>
);

const Breakpoints = () => (
  <Table.Root size="sm" maxW="360px">
    <Table.Header>
      <Table.Row>
        <Table.ColumnHeader>Token</Table.ColumnHeader>
        <Table.ColumnHeader>Min-width</Table.ColumnHeader>
      </Table.Row>
    </Table.Header>
    <Table.Body>
      {Object.entries(breakpointTokens).map(([token, { value }]) => (
        <Table.Row key={token}>
          <Table.Cell>{token}</Table.Cell>
          <Table.Cell>{value}</Table.Cell>
        </Table.Row>
      ))}
    </Table.Body>
  </Table.Root>
);

export const TypographyAudit = () => (
  <Section
    id="typography"
    title="Typography"
    description={`Defaults first — that is what nearly every call site renders.
      The scale below is what you can opt into, read live from the shared theme
      in @ayble-health/dashboard-components, filtered to the steps careOS
      actually uses. Unused sizes and weights are in the Archive tab. Usage
      measured ${TYPE_USAGE_MEASURED_ON}.`}
  >
    <VStack gap={8} align="stretch">
      <VStack gap={2} align="stretch">
        <Heading size="md">Defaults</Heading>
        <Defaults />
      </VStack>

      <VStack gap={2} align="stretch">
        <Heading size="md">Fonts</Heading>
        <Fonts />
      </VStack>

      <VStack gap={2} align="stretch">
        <Heading size="md">Font sizes</Heading>
        <FontSizes />
      </VStack>

      <VStack gap={2} align="stretch">
        <Heading size="md">Font weights</Heading>
        <FontWeights />
      </VStack>

      <VStack gap={2} align="stretch">
        <Heading size="md">Breakpoints</Heading>
        <Breakpoints />
      </VStack>
    </VStack>
  </Section>
);
